import React, { useEffect, useState } from 'react';
import { useReportStore } from '../stores/reportStore';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useRememberedState } from '@redpanda/utils';

const StatCard: React.FC<{ title: string; value: number | string | React.ReactNode; color?: string }> = ({ 
  title, 
  value, 
  color = "text-gray-900 dark:text-white" 
}) => (
  <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow">
    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">{title}</h3>
    <div className={`mt-1 text-3xl font-semibold ${color} min-h-[40px] flex items-center`}>
      {value}
    </div>
  </div>
);

const ReportTable: React.FC<{ title: string; data: any[]; columns: string[]; loading?: boolean }> = ({
  title,
  data,
  columns,
  loading = false
}) => {
  const { t } = useTranslation();

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">{title}</h3>
      </div>
      <div className="overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">{t('common.status.loading')}</div>
        ) : data.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">{t('reports.noData')}</div>
        ) : (
        <table className="min-w-full">
          <thead className="bg-gray-50 dark:bg-slate-700">
            <tr>
              {columns.map((column, index) => (
                <th 
                  key={index} 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-gray-700">
            {data.map((row, index) => (
              <tr key={index}>
                {Object.values(row).map((cell: any, cellIndex) => (
                  <td key={cellIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {typeof cell === 'number' ? Math.round(cell).toLocaleString() : cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  </div>
  );
};

const TABS = ['overview', 'trial-balance', 'income-statement', 'balance-sheet'] as const;
type Tab = typeof TABS[number];

const ReportsPage: React.FC = () => {
  const { t } = useTranslation();
  const { data, fetchReportData } = useReportStore();
  const [loading, setLoading] = useState(false);
  
  const [activeTab, setActiveTab] = useRememberedState<Tab>('accounting-ui:reports.tab', 'overview', TABS);

  useEffect(() => {
    const loadData = async () => {
      if (!data) {
        setLoading(true);
      }
      
      try {
        await fetchReportData();
      } catch (err) {
        console.error('Failed to fetch report data:', err);
        toast.error(t('reports.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [data, fetchReportData]);

  const formatCurrency = (value: number | undefined | null) => {
    if (value === undefined || value === null) return '...';
    const roundedValue = Math.round(value);
    return `$${roundedValue.toLocaleString()}`;
  };

  const getAccountType = (englishType: string) => {
    const typeMap: { [key: string]: string } = {
      'ASSET': t('reports.types.asset'),
      'LIABILITY': t('reports.types.liability'),
      'EQUITY': t('reports.types.equity'),
      'REVENUE': t('reports.types.revenue'),
      'INCOME': t('reports.types.revenue'),
      'EXPENSE': t('reports.types.expense'),
    };
    return typeMap[englishType] || englishType;
  };


  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('reports.title')}</h1>
      </div>

      {/* 標籤頁導航 */}
      <div className="mb-4">
        <div className="flex space-x-2">
          {[
            { id: 'overview', label: t('reports.tabs.overview') },
            { id: 'trial-balance', label: t('reports.tabs.trialBalance') },
            { id: 'income-statement', label: t('reports.tabs.incomeStatement') },
            { id: 'balance-sheet', label: t('reports.tabs.balanceSheet') }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as Tab)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab.id
                  ? 'bg-brand-600 text-white'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {loading || !data ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto"></div>
          <p className="mt-4 text-gray-500 dark:text-gray-400">{t('reports.loadingData')}</p>
        </div>
      ) : (
        <>
          {/* 總覽標籤頁 */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* 統計卡片 */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                  title={t('reports.totalAccounts')}
                  value={data?.accountSummary?.totalAccounts || 0}
                />
                <StatCard
                  title={t('reports.totalJournals')}
                  value={data?.journalSummary?.totalJournals || 0}
                />
                <StatCard
                  title={t('reports.totalInvoices')}
                  value={data?.invoiceSummary?.totalInvoices || 0}
                />
                <StatCard
                  title={t('reports.journalTotalAmount')}
                  value={formatCurrency(data?.journalSummary?.totalAmount)}
                  color="text-green-600"
                />
              </div>

              {/* 發票狀態統計 */}
              {data?.invoiceSummary?.byStatus && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <StatCard
                    title={t('reports.invoiceTotalAmount')}
                    value={formatCurrency(data.invoiceSummary.totalAmount)}
                    color="text-blue-600"
                  />
                  <StatCard
                    title={t('reports.paidAmount')}
                    value={formatCurrency(data.invoiceSummary.paidAmount)}
                    color="text-green-600"
                  />
                  <StatCard
                    title={t('reports.unpaidAmount')}
                    value={formatCurrency(data.invoiceSummary.unpaidAmount)}
                    color="text-orange-600"
                  />
                </div>
              )}

              {/* 會計科目分類統計 */}
              {data?.accountSummary?.byType && (
                <ReportTable
                  title={t('reports.accountTypeSummary')}
                  data={data.accountSummary.byType.map((item: any) => ({
                    [t('reports.columns.accountType')]: item.typeLabel,
                    [t('reports.columns.accountCount')]: item.count,
                    [t('reports.columns.totalBalance')]: formatCurrency(item.totalBalance),
                  }))}
                  columns={[t('reports.columns.accountType'), t('reports.columns.accountCount'), t('reports.columns.totalBalance')]}
                />
              )}

              {/* 發票狀態詳情 */}
              {data?.invoiceSummary?.byStatus && (
                <ReportTable
                  title={t('reports.invoiceStatusSummary')}
                  data={data.invoiceSummary.byStatus.map((item: any) => ({
                    [t('reports.columns.status')]: item.statusLabel,
                    [t('reports.columns.count')]: item.count,
                    [t('reports.columns.totalAmount')]: formatCurrency(item.totalAmount),
                  }))}
                  columns={[t('reports.columns.status'), t('reports.columns.count'), t('reports.columns.totalAmount')]}
                />
              )}
            </div>
          )}

          {/* 試算表標籤頁 */}
          {activeTab === 'trial-balance' && data?.trialBalance && (
            <div className="space-y-6">
              {Object.keys(data.trialBalance.accounts || {}).map((accountType) => (
                <ReportTable
                  key={accountType}
                  title={`${getAccountType(accountType)}${t('reports.accounts')}`}
                  data={data.trialBalance.accounts[accountType].map((account: any) => ({
                    [t('reports.columns.accountCode')]: account.code,
                    [t('reports.columns.accountName')]: account.name,
                    [t('reports.columns.balance')]: formatCurrency(account.balance),
                  }))}
                  columns={[t('reports.columns.accountCode'), t('reports.columns.accountName'), t('reports.columns.balance')]}
                />
              ))}

              {/* 總計 */}
              {data.trialBalance.totals && (
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">{t('reports.accountTypeTotals')}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {Object.entries(data.trialBalance.totals).map(([type, total]: [string, any]) => (
                      <div key={type} className="text-center">
                        <div className="text-sm text-gray-500 dark:text-gray-400">{getAccountType(type)}</div>
                        <div className="text-lg font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(total)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 損益表標籤頁 */}
          {activeTab === 'income-statement' && data?.incomeStatement && (
            <div className="space-y-6">
              {/* 收入 */}
              <ReportTable
                title={t('reports.incomeAccounts')}
                data={data.incomeStatement.income?.accounts?.map((account: any) => ({
                  [t('reports.columns.accountCode')]: account.code,
                  [t('reports.columns.accountName')]: account.name,
                  [t('reports.columns.amount')]: formatCurrency(account.balance),
                })) || []}
                columns={[t('reports.columns.accountCode'), t('reports.columns.accountName'), t('reports.columns.amount')]}
              />

              {/* 費用 */}
              <ReportTable
                title={t('reports.expenseAccounts')}
                data={data.incomeStatement.expenses?.accounts?.map((account: any) => ({
                  [t('reports.columns.accountCode')]: account.code,
                  [t('reports.columns.accountName')]: account.name,
                  [t('reports.columns.amount')]: formatCurrency(account.balance),
                })) || []}
                columns={[t('reports.columns.accountCode'), t('reports.columns.accountName'), t('reports.columns.amount')]}
              />

              {/* 損益摘要 */}
              <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">{t('reports.incomeSummary')}</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('reports.totalIncome')}：</span>
                    <span className="font-semibold text-green-600">
                      {formatCurrency(data.incomeStatement.income?.total)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('reports.totalExpenses')}：</span>
                    <span className="font-semibold text-red-600">
                      {formatCurrency(data.incomeStatement.expenses?.total)}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 dark:border-slate-700 pt-3">
                    <span className="font-bold text-gray-900 dark:text-white">{t('reports.netIncome')}：</span>
                    <span className={`font-bold text-lg ${
                      (data.incomeStatement.netIncome || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatCurrency(data.incomeStatement.netIncome)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 資產負債表標籤頁 */}
          {activeTab === 'balance-sheet' && data?.balanceSheet && (
            <div className="space-y-6">
              {/* 資產 */}
              <ReportTable
                title={t('reports.assetAccounts')}
                data={data.balanceSheet.assets?.accounts?.map((account: any) => ({
                  [t('reports.columns.accountCode')]: account.code,
                  [t('reports.columns.accountName')]: account.name,
                  [t('reports.columns.balance')]: formatCurrency(account.balance),
                })) || []}
                columns={[t('reports.columns.accountCode'), t('reports.columns.accountName'), t('reports.columns.balance')]}
              />

              {/* 負債 */}
              <ReportTable
                title={t('reports.liabilityAccounts')}
                data={data.balanceSheet.liabilities?.accounts?.map((account: any) => ({
                  [t('reports.columns.accountCode')]: account.code,
                  [t('reports.columns.accountName')]: account.name,
                  [t('reports.columns.balance')]: formatCurrency(account.balance),
                })) || []}
                columns={[t('reports.columns.accountCode'), t('reports.columns.accountName'), t('reports.columns.balance')]}
              />

              {/* 權益 */}
              <ReportTable
                title={t('reports.equityAccounts')}
                data={data.balanceSheet.equity?.accounts?.map((account: any) => ({
                  [t('reports.columns.accountCode')]: account.code,
                  [t('reports.columns.accountName')]: account.name,
                  [t('reports.columns.balance')]: formatCurrency(account.balance),
                })) || []}
                columns={[t('reports.columns.accountCode'), t('reports.columns.accountName'), t('reports.columns.balance')]}
              />

              {/* 資產負債表平衡檢查 */}
              <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">{t('reports.balanceSheetBalance')}</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('reports.totalAssets')}：</span>
                    <span className="font-semibold text-blue-600">
                      {formatCurrency(data.balanceSheet.assets?.total)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('reports.totalLiabilities')}：</span>
                    <span className="font-semibold text-red-600">
                      {formatCurrency(data.balanceSheet.liabilities?.total)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('reports.totalEquity')}：</span>
                    <span className="font-semibold text-green-600">
                      {formatCurrency(data.balanceSheet.equity?.total)}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 dark:border-slate-700 pt-3">
                    <span className="font-bold text-gray-900 dark:text-white">{t('reports.liabilitiesPlusEquity')}：</span>
                    <span className="font-bold text-lg text-purple-600">
                      {formatCurrency(data.balanceSheet.totalLiabilitiesAndEquity)}
                    </span>
                  </div>
                  <div className="flex justify-center mt-4">
                    {data.balanceSheet.assets?.total === data.balanceSheet.totalLiabilitiesAndEquity ? (
                      <div className="px-4 py-2 bg-green-100 text-green-800 rounded-md">
                        ✓ {t('reports.balanced')}
                      </div>
                    ) : (
                      <div className="px-4 py-2 bg-red-100 text-red-800 rounded-md">
                        ⚠ {t('reports.unbalanced')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReportsPage;